import cx from 'classnames';
import { capitalize } from 'lodash';
import React from 'react';
import { useSelector } from 'react-redux';
import { getCouponInfo } from '../../../selectors/authorization.js';
import { formatCurrency } from '../../../utils/formUtils.js';
import Button from '../Button.js';
import Icon, { Icons } from '../Icon';
import Carousel from '../carousel/Carousel';
import { Tag, TagColor, TagSize } from '../tag/Tag';
import styles from './SubscriptionCard.module.scss';
import Card, { CardShadow, CardStyles } from './Card';
import { useMediaQueryContext } from '@app/common/design-system/media-query-provider/MediaQueryProvider';

type Subscription = {
    id: string;
    duration: 'monthly';
    size: 'small' | 'medium' | 'large';
    price: number;
    hasFreeTrial: boolean;
    constraintText: string;
    documentConstraintText?: string;
    additionalConstraintText?: string;
    featureCategories: FeatureCategoryData[];
    taxDescription: string;
    currency: string;
};

type FeatureCategoryData = {
    title: string;
    items: FeatureData[];
};

type FeatureData = {
    name: string;
    helpText?: string;
    subText?: string;
};

export type CouponInfo = {
    name: string;
    percent_off: number;
    amount_off?: number;
    duration_in_months: number;
    success: boolean;
};

type PriceProps = {
    price: number;
    taxDescription: string;
    couponInfo: CouponInfo;
    currency: string;
};

export const getPriceWithCoupon = (price: number, couponInfo: CouponInfo): number => {
    let priceWithCoupon = price;
    if (couponInfo.amount_off) {
        priceWithCoupon = price - couponInfo.amount_off / 100;
    } else if (couponInfo.percent_off) {
        priceWithCoupon = price * (1 - couponInfo.percent_off / 100);
    }

    return Math.max(priceWithCoupon, 0);
};

const PriceWithCoupon: React.FC<PriceProps> = ({ price, couponInfo, taxDescription, currency }) => {
    const newPrice = getPriceWithCoupon(price, couponInfo);

    return (
        <>
            <div className={styles.price}>
                <span className={styles.unit}>$</span>
                <span className={styles.amount}>{formatCurrency(newPrice, true)}</span>
            </div>
            <div className={styles.pricePostFix}>
                <span className={styles.crossedOutPrice}>${price}</span>
                <span className={styles.currency}>{currency}</span>
                <span className={styles.tax}>{taxDescription}</span>
            </div>
        </>
    );
};

const Price: React.FC<PriceProps> = ({ price, taxDescription, currency }) => {
    return (
        <>
            <span className={styles.unit}>$</span>
            <span className={styles.amount}>{price}</span>
            <div className={styles.pricePostFix}>
                <span className={styles.currency}>{currency}/mth</span>
                <span className={styles.tax}>({taxDescription})</span>
            </div>
        </>
    );
};

const CouponTag: React.FC<PriceProps> = ({ couponInfo }) => {
    let couponAmount;
    if (couponInfo.amount_off) {
        const amount = couponInfo.amount_off / 100;
        couponAmount = `$${formatCurrency(amount, true)}`;
    } else {
        couponAmount = `${couponInfo.percent_off}%`;
    }
    const discountText = `${couponAmount} off ${
        couponInfo.duration_in_months ? 'for ' + couponInfo.duration_in_months + ' months' : 'forever'
    }`;

    return (
        <Tag
            text={discountText}
            className={styles.discountTag}
            size={TagSize.Small}
            color={TagColor.Green}
            endIcon={<Icon className={styles.tickIcon} icon={Icons.TICK} />}
        />
    );
};

type HeaderProps = { headerButton?: JSX.Element } & Pick<
    Subscription,
    'duration' | 'size' | 'price' | 'taxDescription' | 'currency'
>;

const Header: React.FC<HeaderProps> = ({ headerButton, duration, size, price, taxDescription, currency }) => {
    const couponInfo: CouponInfo = useSelector(getCouponInfo);

    return (
        <div className={styles.header}>
            <h2 className={styles.heading}>{capitalize(size)}</h2>
            <div className={styles.priceContainer}>
                {couponInfo && price ? (
                    <PriceWithCoupon
                        price={price}
                        couponInfo={couponInfo}
                        taxDescription={taxDescription}
                        currency={currency}
                    />
                ) : (
                    <Price price={price} couponInfo={couponInfo} taxDescription={taxDescription} currency={currency} />
                )}
            </div>
            <div className={styles.duration}>Billed {capitalize(duration)}</div>
            {headerButton && headerButton}
            {couponInfo && (
                <>
                    {price > 0 ? (
                        <CouponTag
                            price={price}
                            couponInfo={couponInfo}
                            taxDescription={taxDescription}
                            currency={currency}
                        />
                    ) : (
                        <div className={styles.discountTagWhiteSpace} />
                    )}
                </>
            )}
            <hr />
        </div>
    );
};

type ConstraintTextProps = {
    text: string | string[];
    additionalText?: string;
};

const ConstraintText: React.FC<ConstraintTextProps> = ({ text, additionalText }) => {
    const textArray = Array.isArray(text) ? text : [text];
    return (
        <div className={styles.constraintText}>
            {textArray.map((t, index) => {
                const words = t.split(' ');
                const firstWord = words.slice(0, 1);
                const remainingWords = words.slice(1).join(' ');

                return (
                    <p key={index}>
                        <strong>{firstWord}</strong> {remainingWords}
                    </p>
                );
            })}
            {additionalText && <p>{additionalText}</p>}
        </div>
    );
};

type SubscriptionCardProps = {
    subscription: Subscription;
    handleStartTrial: (subscriptionId: string) => void;
    variant?: 'default' | 'primary' | 'secondary';
    featuresHref: string;
};

const SubscriptionCard: React.FC<SubscriptionCardProps> = ({
    subscription,
    handleStartTrial,
    variant = 'default',
    featuresHref
}) => {
    return (
        <Card
            className={cx(styles.subscriptionCard, {
                [styles.primary]: variant === 'primary'
            })}
            testId={`desktop-subscription-card-${subscription.size.toLowerCase()}`}
            shadow={CardShadow.NONE}
            style={CardStyles.SQUARE}
        >
            {variant === 'primary' && <div className={styles.banner}>Recommended!</div>}
            <Header
                {...subscription}
                headerButton={
                    <Button
                        type="button"
                        primary={variant !== 'secondary'}
                        secondary={variant === 'secondary'}
                        onClick={() => handleStartTrial(subscription.id)}
                    >
                        Start{subscription.hasFreeTrial && ' Free Trial'}
                    </Button>
                }
            />

            <ConstraintText text={[subscription.constraintText, subscription.documentConstraintText].filter(Boolean)} />
            <Button type="button" link href={featuresHref} target="_blank">
                View all features
            </Button>
        </Card>
    );
};

type SubscriptionCardGroupProps = {
    className?: string;
    subscriptions: Subscription[];
    handleStartTrial: (subscriptionId: string) => void;
    recommendedSubscriptionIndex?: number;
    featuresHref: string;
};

/** Returns a group of subscription cards in a horizontal row*/
const DesktopSubscriptionCardGroup: React.FC<SubscriptionCardGroupProps> = ({
    className,
    subscriptions,
    handleStartTrial,
    recommendedSubscriptionIndex,
    featuresHref
}) => {
    return (
        <Carousel
            ariaLabel="Subscription plans carousel"
            carouselOptions={{ align: 'start', containScroll: 'trimSnaps' }}
            className={className}
            navigationButtons
            navigationButtonsPosition="top"
            slideContainerClassName={styles.carouselSlideContainer}
            focusedItemIndex={recommendedSubscriptionIndex}
        >
            {subscriptions.map((subscription, index) => {
                return (
                    <SubscriptionCard
                        key={index}
                        subscription={subscription}
                        variant={getSubscriptionCardVariant(index, recommendedSubscriptionIndex)}
                        handleStartTrial={handleStartTrial}
                        featuresHref={featuresHref}
                    />
                );
            })}
        </Carousel>
    );
};

/** Returns a group of subscription cards in a carousel */
const MobileSubscriptionCardGroup: React.FC<SubscriptionCardGroupProps> = ({
    className,
    subscriptions,
    handleStartTrial,
    recommendedSubscriptionIndex,
    featuresHref
}) => {
    return (
        <Carousel
            ariaLabel="Subscription plans carousel"
            className={className}
            navigationDots
            slideContainerClassName={styles.carouselSlideContainer}
            carouselOptions={{ align: 'start' }}
            focusedItemIndex={recommendedSubscriptionIndex}
        >
            {subscriptions.map((subscription, index) => (
                <SubscriptionCard
                    key={index}
                    subscription={subscription}
                    handleStartTrial={handleStartTrial}
                    variant={getSubscriptionCardVariant(index, recommendedSubscriptionIndex)}
                    featuresHref={featuresHref}
                />
            ))}
        </Carousel>
    );
};

const SubscriptionCardGroup: React.FC<SubscriptionCardGroupProps> = props => {
    const { isMobile } = useMediaQueryContext();

    return isMobile ? <MobileSubscriptionCardGroup {...props} /> : <DesktopSubscriptionCardGroup {...props} />;
};

export { SubscriptionCardGroup };
export type { Subscription, FeatureData };

function getSubscriptionCardVariant(index: number, recommendedSubscriptionIndex: number | undefined) {
    if (recommendedSubscriptionIndex === undefined) {
        return undefined;
    }
    return index === recommendedSubscriptionIndex ? 'primary' : 'secondary';
}
